/**
 * Lehký, CSP-safe syntax highlighter pro help center code bloky.
 *
 * Nezávislý na highlight.js (žádný `eval`, žádný externí zdroj) — tokenizuje
 * `textContent` už serverem vyrenderovaného `<pre><code class="language-X">`
 * bloku a nahradí jeho `innerHTML` obarvenými `<span class="hl-*">`. Veškerý
 * text se HTML-escapuje, takže obohacení nemůže injektovat markup.
 *
 * Pokrývá jazyky reálně používané v docs: bash, json, yaml, java. Neznámý
 * jazyk → jen escapovaný text (žádné obarvení, ale bez rozbití).
 */

type Rule = { re: RegExp; cls: string };

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Sticky (`y`) regexy — matchují jen na aktuální pozici. Pořadí = priorita.
const RULES: Record<string, Rule[]> = {
  bash: [
    { re: /#.*/y, cls: "comment" },
    { re: /"(?:[^"\\]|\\.)*"/y, cls: "string" },
    { re: /'[^']*'/y, cls: "string" },
    { re: /\$\{?[A-Za-z_][A-Za-z0-9_]*\}?/y, cls: "variable" },
    { re: /\b(?:curl|docker|docker-compose|export|source|cd|sudo|echo|npm|mvn|git|kubectl|if|then|fi|for|do|done|else|elif|while|case|esac|function|return)\b/y, cls: "keyword" },
    { re: /\b\d+\b/y, cls: "number" },
  ],
  json: [
    { re: /"(?:[^"\\]|\\.)*"(?=\s*:)/y, cls: "key" },
    { re: /"(?:[^"\\]|\\.)*"/y, cls: "string" },
    { re: /\b(?:true|false|null)\b/y, cls: "boolean" },
    { re: /-?\b\d+(?:\.\d+)?(?:[eE][+-]?\d+)?\b/y, cls: "number" },
  ],
  yaml: [
    { re: /#.*/y, cls: "comment" },
    { re: /"(?:[^"\\]|\\.)*"/y, cls: "string" },
    { re: /'[^']*'/y, cls: "string" },
    { re: /\b(?:true|false|null|yes|no)\b/y, cls: "boolean" },
    { re: /-?\b\d+(?:\.\d+)?\b/y, cls: "number" },
  ],
  java: [
    { re: /\/\/.*/y, cls: "comment" },
    { re: /\/\*[\s\S]*?\*\//y, cls: "comment" },
    { re: /"(?:[^"\\]|\\.)*"/y, cls: "string" },
    { re: /@[A-Za-z_][A-Za-z0-9_]*/y, cls: "keyword" },
    { re: /\b(?:public|private|protected|class|interface|enum|record|void|return|new|import|package|static|final|abstract|int|long|double|float|char|byte|short|boolean|var|String|if|else|for|while|do|switch|case|break|continue|try|catch|finally|throw|throws|extends|implements|this|super|instanceof)\b/y, cls: "keyword" },
    { re: /\b(?:true|false|null)\b/y, cls: "boolean" },
    { re: /\b\d+(?:\.\d+)?[LlFfDd]?\b/y, cls: "number" },
  ],
};

const ALIASES: Record<string, string> = {
  sh: "bash",
  shell: "bash",
  zsh: "bash",
  console: "bash",
  yml: "yaml",
  jsonc: "json",
};

export function highlightCode(text: string, lang: string): string {
  const rules = RULES[ALIASES[lang] ?? lang];
  if (!rules) return escapeHtml(text);
  let out = "";
  let i = 0;
  while (i < text.length) {
    let matched = false;
    for (const { re, cls } of rules) {
      re.lastIndex = i;
      const m = re.exec(text);
      if (m && m.index === i && m[0].length > 0) {
        out += `<span class="hl-${cls}">${escapeHtml(m[0])}</span>`;
        i += m[0].length;
        matched = true;
        break;
      }
    }
    if (!matched) {
      out += escapeHtml(text[i]);
      i += 1;
    }
  }
  return out;
}

/** Extract `language-X` class → `X`, or null. */
function langOf(el: Element): string | null {
  for (const c of el.classList) {
    if (c.startsWith("language-")) return c.slice("language-".length);
  }
  return null;
}

/**
 * Projde kontejner a obarví každý `<code class="language-*">` blok. Idempotentní
 * přes `data-hl` guard (React může content přerenderovat). Volat z useEffect
 * po vložení server HTML.
 */
export function highlightContainer(root: HTMLElement | null): void {
  if (!root) return;
  const blocks = root.querySelectorAll<HTMLElement>("pre code[class*='language-']");
  blocks.forEach((code) => {
    if (code.dataset.hl === "1") return;
    const lang = langOf(code);
    if (!lang || !(RULES[ALIASES[lang] ?? lang])) return;
    code.innerHTML = highlightCode(code.textContent ?? "", lang);
    code.dataset.hl = "1";
  });
}
